// engine-settings.ts — the business parameters of the quote engine, read from the settings tables.
//
// Nothing in here has a default. Every value comes from `app_kv_settings` (keys `quote_engine.*`),
// from the `app_settings` singleton (technical margins) or from `quote_workshops` (lead times), and the
// parser REFUSES an absent or malformed value: a missing setting must stop the computation loudly, never
// be replaced by a plausible number. The seed values live in the migration
// `20261012110000_quote_engine_settings.sql` (and in `testing/engine-settings.seed.ts` for the specs).

import type { DistributorRule } from "../lib/ops/distributors";
import type { TechnicalMargins } from "../lib/ops/material-format";
import type { MaterialNature } from "../lib/pricing/pa-material";

/** Keys of the engine settings in `app_kv_settings`. */
export const ENGINE_SETTING_KEYS = {
  studioRateCard: "quote_engine.studio_rate_card",
  distributorRules: "quote_engine.distributor_rules",
  materialNature: "quote_engine.material_nature",
  paymentTerms: "quote_engine.payment_terms",
  agencyDiscount: "quote_engine.agency_discount",
  backPlanning: "quote_engine.back_planning",
  parameters: "quote_engine.parameters",
} as const;

/** All keys, for one `in (...)` query. */
export const ENGINE_SETTING_KEY_LIST: readonly string[] = Object.values(ENGINE_SETTING_KEYS);

/** Rate and minimum of one studio billing mode. */
export interface StudioRate {
  /** Unit rate in euros. */
  rate: number;
  /** Minimum billed in euros. */
  min: number;
}

/** The studio rate schedule (graphic design and packaging each bill in two exclusive ways). */
export interface StudioRateCard {
  info: { file: StudioRate; hourly: StudioRate };
  packaging: { element: StudioRate; hourly: StudioRate };
  /** Advanced graphic design: always hourly, no minimum. */
  advancedInfo: { rate: number };
}

/** A distributor rule as stored: scope fields plus the nature shortcut (`natures`). */
export type StoredDistributorRule = Omit<DistributorRule, "families"> & {
  families?: readonly string[];
  /** Expands to every material group of the given natures (single source: the material nature map). */
  natures?: readonly MaterialNature[];
};

/** One payment term: the code mirrors the DB check constraint on clients / quotes. */
export interface PaymentTermSetting {
  code: string;
  /** Money due at order time (drives the deposit alert). */
  deposit: boolean;
}

/** Agency discount entry range and presets. */
export interface AgencyDiscountSetting {
  /** Highest agency discount accepted at entry, in percent. */
  maxPct: number;
  presets: readonly { code: string; pct: number }[];
}

/** Default back-planning offsets, in working days before delivery. */
export interface BackPlanningSetting {
  fileReceptionDays: number;
  batValidationDays: number;
}

/** Scalar business parameters of the engine. */
export interface EngineParameters {
  /** Cap of the composed line discount (tier x client), as a rate. */
  discountCapRate: number;
  /** Minimum billed surface of a custom size, in m2. */
  customSizeMinBilledM2: number;
  /** Largest height added to a base reference before custom size is closed, in mm. */
  customSizeMaxMarginHeightMm: number;
  /** Largest width added to a base reference before custom size is closed, in mm. */
  customSizeMaxMarginWidthMm: number;
  /** Cumulated surface under which a small-volume workshop suggestion applies, in m2. */
  smallVolumeThresholdM2: number;
  /** Surface under which the minimum flat fee purchase price is scaled up, in m2. */
  minFlatFeeM2: number;
  /** Web width from which a format counts as a wide web, in mm. */
  wideWebThresholdMm: number;
  /** Cut codes common to every material collection. */
  commonCutCodes: readonly string[];
}

/** Everything the engine reads from the settings tables. */
export interface EngineSettings {
  studioRateCard: StudioRateCard;
  distributorRules: readonly StoredDistributorRule[];
  /** Waste nature by material group (reference prefix). */
  materialNature: Readonly<Record<string, MaterialNature>>;
  paymentTerms: readonly PaymentTermSetting[];
  agencyDiscount: AgencyDiscountSetting;
  backPlanning: BackPlanningSetting;
  /** From the `app_settings` singleton. */
  technicalMargins: TechnicalMargins;
  parameters: EngineParameters;
}

/** A setting that is absent or malformed. */
export class EngineSettingError extends Error {
  /**
   * @param key - Setting key (or column) at fault.
   * @param path - Path of the faulty value inside the setting.
   * @param expected - What was expected there.
   */
  constructor(
    readonly key: string,
    readonly path: string,
    readonly expected: string,
  ) {
    super(`Engine setting "${key}" is missing or malformed at "${path}": expected ${expected}.`);
    this.name = "EngineSettingError";
  }
}

/**
 * Walks to a path of a parsed setting.
 * @param root - The setting value.
 * @param path - Dotted path ("" = the value itself).
 * @returns The value found, or undefined.
 */
function at(root: unknown, path: string): unknown {
  let cur: unknown = root;
  for (const part of path ? path.split(".") : []) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

/**
 * Reads a finite number from a setting.
 * @param key - Setting key.
 * @param root - The setting value.
 * @param path - Path inside it.
 * @param min - Smallest accepted value (inclusive).
 * @returns The number.
 * @throws EngineSettingError when absent, not a finite number or below `min`.
 */
function num(key: string, root: unknown, path: string, min = 0): number {
  const v = at(root, path);
  if (typeof v !== "number" || !Number.isFinite(v) || v < min) throw new EngineSettingError(key, path, `a number >= ${min}`);
  return v;
}

/**
 * Reads a non-empty array of strings from a setting.
 * @param key - Setting key.
 * @param root - The setting value.
 * @param path - Path inside it.
 * @returns The strings.
 * @throws EngineSettingError when absent or not an array of non-empty strings.
 */
function strings(key: string, root: unknown, path: string): string[] {
  const v = at(root, path);
  if (!Array.isArray(v) || v.length === 0 || v.some((s) => typeof s !== "string" || !s)) {
    throw new EngineSettingError(key, path, "a non-empty array of strings");
  }
  return v as string[];
}

/**
 * Reads a required setting value.
 * @param kv - Setting values by key.
 * @param key - Setting key.
 * @returns The value.
 * @throws EngineSettingError when the key is absent.
 */
function required(kv: Readonly<Record<string, unknown>>, key: string): unknown {
  const v = kv[key];
  if (v == null) throw new EngineSettingError(key, "", "a value (row missing in app_kv_settings)");
  return v;
}

/**
 * Builds the engine settings from the raw setting rows, refusing anything absent or malformed.
 * @param kv - `app_kv_settings` values by key (every key of {@link ENGINE_SETTING_KEYS}).
 * @param technicalMargins - Technical margins read from the `app_settings` singleton.
 * @returns The validated settings.
 * @throws EngineSettingError naming the first absent or malformed value.
 */
export function engineSettingsFromKv(
  kv: Readonly<Record<string, unknown>>,
  technicalMargins: TechnicalMargins,
): EngineSettings {
  const studioKey = ENGINE_SETTING_KEYS.studioRateCard;
  const studio = required(kv, studioKey);
  /**
   * Reads one studio rate.
   * @param path - Path of the rate.
   * @returns The rate and minimum.
   */
  const rate = (path: string): StudioRate => ({ rate: num(studioKey, studio, `${path}.rate`), min: num(studioKey, studio, `${path}.min`) });
  const studioRateCard: StudioRateCard = {
    info: { file: rate("info.file"), hourly: rate("info.hourly") },
    packaging: { element: rate("packaging.element"), hourly: rate("packaging.hourly") },
    advancedInfo: { rate: num(studioKey, studio, "advancedInfo.rate") },
  };

  const rulesKey = ENGINE_SETTING_KEYS.distributorRules;
  const rules = required(kv, rulesKey);
  if (!Array.isArray(rules) || rules.length === 0) throw new EngineSettingError(rulesKey, "", "a non-empty array of rules");
  const distributorRules = rules.map((r, i): StoredDistributorRule => {
    const distributor = at(r, "distributor");
    if (typeof distributor !== "string" || !distributor) throw new EngineSettingError(rulesKey, `${i}.distributor`, "a supplier name");
    const rule: StoredDistributorRule = { distributor, ...(at(r, "isDefault") === true ? { isDefault: true } : {}) };
    for (const field of ["refs", "brands", "categories", "families", "natures"] as const) {
      if (at(r, field) !== undefined) Object.assign(rule, { [field]: strings(rulesKey, r, field) });
    }
    return rule;
  });

  const natureKey = ENGINE_SETTING_KEYS.materialNature;
  const natureRaw = required(kv, natureKey);
  const materialNature: Record<string, MaterialNature> = {};
  for (const group of Object.keys(natureRaw as object)) {
    const nature = at(natureRaw, group);
    if (nature !== "rigid" && nature !== "flexible" && nature !== "none") throw new EngineSettingError(natureKey, group, "rigid | flexible | none");
    materialNature[group.toUpperCase()] = nature;
  }
  if (Object.keys(materialNature).length === 0) throw new EngineSettingError(natureKey, "", "at least one material group");

  const termsKey = ENGINE_SETTING_KEYS.paymentTerms;
  const termsRaw = required(kv, termsKey);
  if (!Array.isArray(termsRaw) || termsRaw.length === 0) throw new EngineSettingError(termsKey, "", "a non-empty array of terms");
  const paymentTerms = termsRaw.map((t, i): PaymentTermSetting => {
    const code = at(t, "code");
    if (typeof code !== "string" || !code) throw new EngineSettingError(termsKey, `${i}.code`, "a term code");
    if (typeof at(t, "deposit") !== "boolean") throw new EngineSettingError(termsKey, `${i}.deposit`, "a boolean");
    return { code, deposit: at(t, "deposit") as boolean };
  });

  const agencyKey = ENGINE_SETTING_KEYS.agencyDiscount;
  const agencyRaw = required(kv, agencyKey);
  const presetsRaw = at(agencyRaw, "presets");
  if (!Array.isArray(presetsRaw)) throw new EngineSettingError(agencyKey, "presets", "an array");
  const agencyDiscount: AgencyDiscountSetting = {
    maxPct: num(agencyKey, agencyRaw, "maxPct"),
    presets: presetsRaw.map((p, i) => {
      const code = at(p, "code");
      if (typeof code !== "string" || !code) throw new EngineSettingError(agencyKey, `presets.${i}.code`, "a preset code");
      return { code, pct: num(agencyKey, p, "pct") };
    }),
  };

  const planKey = ENGINE_SETTING_KEYS.backPlanning;
  const planRaw = required(kv, planKey);
  const backPlanning: BackPlanningSetting = {
    fileReceptionDays: num(planKey, planRaw, "fileReceptionDays", 1),
    batValidationDays: num(planKey, planRaw, "batValidationDays", 1),
  };

  const paramKey = ENGINE_SETTING_KEYS.parameters;
  const p = required(kv, paramKey);
  const parameters: EngineParameters = {
    discountCapRate: num(paramKey, p, "discountCapRate"),
    customSizeMinBilledM2: num(paramKey, p, "customSizeMinBilledM2"),
    customSizeMaxMarginHeightMm: num(paramKey, p, "customSizeMaxMarginHeightMm"),
    customSizeMaxMarginWidthMm: num(paramKey, p, "customSizeMaxMarginWidthMm"),
    smallVolumeThresholdM2: num(paramKey, p, "smallVolumeThresholdM2"),
    minFlatFeeM2: num(paramKey, p, "minFlatFeeM2", Number.MIN_VALUE),
    wideWebThresholdMm: num(paramKey, p, "wideWebThresholdMm"),
    commonCutCodes: strings(paramKey, p, "commonCutCodes"),
  };

  return {
    studioRateCard,
    distributorRules,
    materialNature,
    paymentTerms,
    agencyDiscount,
    backPlanning,
    technicalMargins,
    parameters,
  };
}
